import { Singleton } from '@visiba/instantiation';

@Singleton()
export class PlatformService {
	public static get runtimeIsInBrowser(): boolean {
		return typeof navigator !== 'undefined' && typeof window !== 'undefined';
	}

	public static get runtimeSupportsTouch(): boolean {
		return 'ontouchstart' in window || window.navigator.maxTouchPoints > 0;
	}

	public get runtimeIsInBrowser(): boolean {
		return PlatformService.runtimeIsInBrowser;
	}

	public get runtimeSupportsTouch(): boolean {
		return PlatformService.runtimeSupportsTouch;
	}

	constructor() {
		// Empty
	}

	public browserIs(test: number): boolean {
		const statements: boolean[] = [];
		const {
			browsers: { chrome, edge, firefox, ie, opera, safari, samsungBrowser, strictSafari },
		} = this.parseAgentData();

		if ((test & _BrowserList.Edge) === _BrowserList.Edge) {
			statements.push(edge);
		}

		if ((test & _BrowserList.SamsungBrowser) === _BrowserList.SamsungBrowser) {
			statements.push(samsungBrowser);
		}

		if ((test & _BrowserList.Opera) === _BrowserList.Opera) {
			statements.push(opera);
		}

		if ((test & _BrowserList.Firefox) === _BrowserList.Firefox) {
			statements.push(firefox);
		}

		if ((test & _BrowserList.Chrome) === _BrowserList.Chrome) {
			statements.push(chrome);
		}

		if ((test & _BrowserList.Safari) === _BrowserList.Safari) {
			statements.push(safari);
		}

		if ((test & _BrowserList.StrictSafari) === _BrowserList.StrictSafari) {
			statements.push(strictSafari);
		}

		if ((test & _BrowserList.IE) === _BrowserList.IE) {
			statements.push(ie);
		}

		return statements.filter(Boolean).length > 0;
	}

	public parseAgentData(): Readonly<UserAgentData> {
		const edgeHTML = this.runtimeIsInBrowser && navigator.userAgent.includes('Edge/');
		const edgeChromium = this.runtimeIsInBrowser && navigator.userAgent.includes('Edg/');
		const edgeAndroid = this.runtimeIsInBrowser && navigator.userAgent.includes('EdgA/');
		const edgeIos = this.runtimeIsInBrowser && navigator.userAgent.includes('EdgiOS/');
		const edge = edgeHTML || edgeChromium || edgeAndroid || edgeIos;
		const samsungBrowser = this.runtimeIsInBrowser && navigator.userAgent.includes('SamsungBrowser/');
		const opera = this.runtimeIsInBrowser && (navigator.userAgent.includes('Opera') || navigator.userAgent.includes('OPR/'));
		const firefoxIos = this.runtimeIsInBrowser && navigator.userAgent.includes('FxiOS/');
		const firefox = this.runtimeIsInBrowser && (navigator.userAgent.includes('Firefox') || firefoxIos);
		const chromeIos = this.runtimeIsInBrowser && navigator.userAgent.includes('CriOS/');
		const chrome =
			this.runtimeIsInBrowser && (navigator.userAgent.includes('Chrome') || chromeIos) && !edge && !opera && !samsungBrowser;
		const safari =
			(this.runtimeIsInBrowser &&
				navigator.userAgent.includes('Safari') &&
				!chrome &&
				!edgeChromium &&
				!firefox &&
				!opera &&
				!samsungBrowser) ||
			edgeIos ||
			chromeIos ||
			firefoxIos;
		const strictSafari =
			this.runtimeIsInBrowser && navigator.userAgent.includes('Safari') && !chrome && !edge && !firefox && !opera && !samsungBrowser;
		const ie = this.runtimeIsInBrowser && navigator.userAgent.includes('Trident');

		const browsers = Object.freeze({
			edgeChromium,
			edgeAndroid,
			edgeIos,
			firefoxIos,
			chromeIos,
			edge,
			samsungBrowser,
			opera,
			firefox,
			chrome,
			safari,
			strictSafari,
			ie,
		});

		return Object.freeze({
			browsers,
		});
	}
}

interface BrowsersData {
	edgeChromium: boolean;
	edgeAndroid: boolean;
	edgeIos: boolean;
	edge: boolean;
	samsungBrowser: boolean;
	opera: boolean;
	firefoxIos: boolean;
	firefox: boolean;
	chromeIos: boolean;
	chrome: boolean;
	safari: boolean;
	strictSafari: boolean;
	ie: boolean;
}

interface UserAgentData {
	browsers: Readonly<BrowsersData>;
}

// prettier-ignore
export enum _BrowserList {
	None            = 0,      // 0 - Runtime is in terminal
	Edge            = 1 << 0, // 1
	Safari          = 1 << 1, // 2
	StrictSafari    = 1 << 2, // 4 - All browsers within iOS is using Safari in the background, but this flag ignores that fact.
	Firefox         = 1 << 3, // 8
	Chrome          = 1 << 4, // 16
	SamsungBrowser  = 1 << 5, // 32
	Opera           = 1 << 6, // 64
	IE              = 1 << 7, // 128
}
